import { DateRange } from '@mui/lab';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, FormControl } from '@mui/material';
import { InvoiceDate } from 'models/invoice';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DateRangeFilterProps = {
    onDateRangeFilterChange: (dateRange: DateRange<Date>) => void;
    dateRangeValue: DateRange<Date | null>;
    dateFilterType: string;
};

const DateRangeFilter: FC<DateRangeFilterProps> = ({
    onDateRangeFilterChange,
    dateRangeValue,
    dateFilterType,
}: DateRangeFilterProps) => {
    const { t: translate } = useTranslation(['common']);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);

    useEffect(() => {
        if (dateRangeValue.length) {
            const [incomingFromDate, incomingToDate] = dateRangeValue;
            const isInvalidRange = incomingFromDate && incomingToDate && incomingFromDate > incomingToDate;
            if (isInvalidRange) {
                setFromDate(null);
                setToDate(null);
                onDateRangeFilterChange([null, null]);
            } else {
                setFromDate(incomingFromDate);
                setToDate(incomingToDate);
            }
        }
    }, [dateRangeValue]);

    const handleFromDateChange = (date: Date | null) => {
        setFromDate(date);
        const isToDateBeforeFromDate = date && toDate && date > toDate;
        if (isToDateBeforeFromDate) {
            setToDate(null);
            onDateRangeFilterChange([date, null]);
        } else if (date && toDate) {
            onDateRangeFilterChange([date, toDate]);
        }
    };
    const handleToDateChange = (date: Date | null) => {
        setToDate(date);
        if (fromDate && date) {
            onDateRangeFilterChange([fromDate, date]);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
            <Box display="flex">
                <FormControl fullWidth sx={{ marginRight: 1 }}>
                    <DatePicker
                        format={getDatePickerFormat()}
                        disabled={dateFilterType === InvoiceDate.NO_DATE_FILTER}
                        onChange={handleFromDateChange}
                        label={translate('orders:DATE_FROM')}
                        value={fromDate}
                        maxDate={toDate || undefined}
                    />
                </FormControl>
                <FormControl fullWidth>
                    <DatePicker
                        format={getDatePickerFormat()}
                        disabled={dateFilterType === InvoiceDate.NO_DATE_FILTER}
                        onChange={handleToDateChange}
                        label={translate('orders:DATE_TO')}
                        value={toDate}
                        minDate={fromDate || undefined}
                    />
                </FormControl>
            </Box>
        </LocalizationProvider>
    );
};

export default DateRangeFilter;
